import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { sendErrorToast, sendSuccessToast } from 'app/shared/toasts/actions';
import { ALERT_QUERY_KEYS } from 'app/modules/alerts/queries/keys';
import { post } from 'app/shared/utils/fetchr';
import { InvestigationActionPayload } from 'app/modules/investigations/types/requests';
import { CHECKLIST_QUERY_KEYS } from 'app/shared/customChecklist/queries/keys';
import { AI_AGENT_RESULTS_QUERY_KEYS } from 'app/modules/aiAgentResults/queries/keys';
import { selectDLEnabledForAlertDetails } from 'app/shared/featureFlags/selectors';

export const useRequeueAlert = (ids: number[]) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const useNewEndpoint = useSelector(selectDLEnabledForAlertDetails);
  return useMutation({
    mutationFn: (queueID: number) =>
      post(
        useNewEndpoint
          ? '/v1/investigations/cached/action'
          : '/v1/investigations/action',
        {
          object_ids: ids,
          object_type: 'ALERT',
          investigation_queue_id: queueID,
        } satisfies InvestigationActionPayload,
      ),
    onError: () =>
      dispatch(
        sendErrorToast('Failed to change alert queue. Please try again.'),
      ),
    onSuccess: () => {
      dispatch(sendSuccessToast('Successfully changed alert queue.'));
      ids.forEach((i) => {
        queryClient.invalidateQueries({
          queryKey: ALERT_QUERY_KEYS.getAlert(i, true),
        });
        queryClient.invalidateQueries({
          queryKey: ALERT_QUERY_KEYS.getAlert(i, false),
        });
        // checklist might change since its queue-based
        queryClient.invalidateQueries({
          queryKey: CHECKLIST_QUERY_KEYS.getChecklistSubmission(i),
        });
        // Can-start-review depends on the alert's queue (and that queue's assigned AI agent).
        queryClient.invalidateQueries({
          queryKey: AI_AGENT_RESULTS_QUERY_KEYS.canStartReview({
            relation_id: i.toString(),
            relation_type: 'ALERT',
          }),
        });
      });
    },
  });
};
